import { useRollbar } from '@rollbar/react'
import { ButtonActionTypeEnum } from 'common/enums/ButtonActionTypeEnum'
import { BadRequest, NetworkError } from 'common/errors'
import { ButtonInterface } from 'common/types/entities/button-interface'
import { MemberSubmitData, sendForm } from 'publisher/api/member-access'
import { useState } from 'react'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { useDispatch } from 'react-redux'
import { extractResetToken } from 'src/publisher/utils/extract-reset-token'
import { optInFail } from '../actions/optInActions'
import { validateEmptyOptInFields } from '../utils/fieldValidation'
import useManagement, { selectors as managementSel } from './useManagement'
import useOptIn, { selectors as optInSelectors } from './useOptIn'

const useLoginSubmit = (button: ButtonInterface) => {
  const rollbar = useRollbar()
  const { t } = useTypedTranslation()
  const dispatch = useDispatch()
  const [isLoading, setLoading] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const fields = useOptIn(optInSelectors.getCustomerOptInFields)
  const confirmedExplicitConsentsIds = useManagement(
    managementSel.getConfirmedExplicitConsentsIds,
  ).length
  const rememberMe = confirmedExplicitConsentsIds > 0
  const calculatedEmail = fields.email ? fields.email : ''
  const calculatedPassword = fields.password ? fields.password : ''
  let formData: MemberSubmitData
  let apiUrl = typeof window !== 'undefined' ? location.href : ''
  const confirmRegistrationToken =
    typeof window !== 'undefined'
      ? window.location.pathname.split('/').pop()
      : ''
  let redirectUrl = ''

  switch (button.action) {
    case 'login':
      redirectUrl = '/school'
      apiUrl = '/api/security/login'
      formData = {
        email: calculatedEmail,
        password: calculatedPassword,
        rememberMe: rememberMe,
      }
      break
    case 'confirmRegistration':
      apiUrl = '/api/security/register/confirm'
      formData = {
        plainPassword: calculatedPassword,
        firstName: fields.first_name,
        lastName: fields.surname,
        token: confirmRegistrationToken as string,
      }
      break
    case 'resetPassword':
      apiUrl = '/api/security/reset'
      formData = {
        token: extractResetToken(),
        password: calculatedPassword,
      }
      break
    case 'resetRequest':
      redirectUrl = '/dashboard/check-email'
      apiUrl = '/api/security/send-reset'
      formData = {
        email: calculatedEmail,
      }
      break
    default:
      rollbar.error(
        `Unknown button action type in custom login form ${button.action}`,
      )
  }

  const submit = async () => {
    const slugs = Object.keys(fields)
    const fieldsForValidationFormat = slugs.reduce((acc, slug) => {
      return { ...acc, [slug]: { value: fields[slug] } }
    }, {})
    const emptyFieldErrors = validateEmptyOptInFields(
      fieldsForValidationFormat,
      t,
    )
    if (Object.keys(emptyFieldErrors).length) {
      const common: string[] = []
      dispatch(optInFail({ common, fields: emptyFieldErrors }))
      return
    }

    if (
      button.action === ButtonActionTypeEnum.ConfirmRegistration &&
      calculatedPassword !== fields.confirm_password
    ) {
      const common: string[] = []
      dispatch(
        optInFail({
          common,
          fields: { confirm_password: [t('validation.password.mismatch')] },
        }),
      )
      return
    }

    setLoading(true)
    try {
      const response = await sendForm(apiUrl, formData)
      window.location.replace(redirectUrl || response.data.redirect)
    } catch (error) {
      if (error instanceof BadRequest) {
        setErrors(error.response.data.errors.common)
        const fieldsErrors = error.response.data.errors.fields || {}
        dispatch(
          optInFail({
            fields: {
              ...fieldsErrors,
              password:
                fieldsErrors.plainPassword || fieldsErrors.password || [],
              surname: fieldsErrors.lastName || fieldsErrors.surname || [],
              first_name:
                fieldsErrors.firstName || fieldsErrors.first_name || [],
            },
          }),
        )
        window.dispatchEvent(new Event('form_updated'))
      } else if (error instanceof NetworkError) {
        setErrors([t('core.errors.no_connection')])
      } else {
        rollbar.error(`Custom login error`, error as Error)
      }
    }
    setLoading(false)
  }

  return [isLoading, errors, submit]
}

export default useLoginSubmit
